import { useRef, useState, useCallback } from 'react';
import { IOrders } from '../pages/Dashboard/order.types';
import { ISettings, IStadium } from '../pages/Settings/settings.types';

interface IPrintOrder {
  ip_printer?: string;
  data?: IOrders;
  dataSettings?: ISettings;
  stadium?: IStadium;
  type?: string;
  printer_brand?: 'star' | 'epson';
}

interface PrintQueueItem extends IPrintOrder {
  id: string;
  attempts: number;
  lastAttemptTime?: number;
}

const RETRY_CONFIG = {
  maxAttempts: 5,
  retryDelayMs: 5000,
  minDelayBetweenAddsMs: 500,
  minDelayBetweenPrintsMs: 800,
  processTimeoutMs: 30000, // per-item processing timeout (ms)
};

export const usePrinterQueue = () => {
  const printQueueRef = useRef<PrintQueueItem[]>([]);
  const isPrintingRef = useRef(false);
  const lastAddTimeRef = useRef<number>(0);
  const lastPrintCompletionRef = useRef<number>(0);

  const [isPrinting, setIsPrinting] = useState(false);
  const [queueLength, setQueueLength] = useState(0);
  const [currentPrintOrderId, setCurrentPrintOrderId] = useState<string | null>(null);

  const generateQueueId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const addToQueue = useCallback(
    (order: IPrintOrder): string => {
      const now = Date.now();
      const queueItem: PrintQueueItem = {
        ...order,
        id: generateQueueId(),
        attempts: 0,
      };

      printQueueRef.current.push(queueItem);
      setQueueLength(printQueueRef.current.length);
      lastAddTimeRef.current = now;

      return queueItem.id;
    },
    [generateQueueId]
  );

  const processQueue = useCallback(async (onProcessItem: (item: PrintQueueItem) => Promise<void>): Promise<void> => {
    if (isPrintingRef.current || printQueueRef.current.length === 0) {
      return;
    }

    isPrintingRef.current = true;
    setIsPrinting(true);

    // Ensure onProcessItem does not hang indefinitely by racing with a timeout
    const withTimeout = <T>(p: Promise<T>, ms: number) =>
      new Promise<T>((resolve, reject) => {
        let finished = false;
        const timer = setTimeout(() => {
          if (!finished) {
            finished = true;
            reject(new Error('process_timeout'));
          }
        }, ms);
        p.then((v) => {
          if (!finished) {
            finished = true;
            clearTimeout(timer);
            resolve(v);
          }
        }).catch((err) => {
          if (!finished) {
            finished = true;
            clearTimeout(timer);
            reject(err);
          }
        });
      });

    try {
      // Process all items in the queue before releasing the lock.
      // Using a while loop instead of recursion so that the guard at the top
      // (isPrintingRef.current) never blocks the next item from being processed.
      while (printQueueRef.current.length > 0) {
        const item = printQueueRef.current[0];
        setCurrentPrintOrderId(item.id);

        try {
          const timeSinceLastPrint = Date.now() - lastPrintCompletionRef.current;
          if (lastPrintCompletionRef.current > 0 && timeSinceLastPrint < RETRY_CONFIG.minDelayBetweenPrintsMs) {
            const waitTime = RETRY_CONFIG.minDelayBetweenPrintsMs - timeSinceLastPrint;
            await new Promise((resolve) => setTimeout(resolve, waitTime));
          }

          item.attempts++;
          item.lastAttemptTime = Date.now();

          await withTimeout(onProcessItem(item), RETRY_CONFIG.processTimeoutMs);

          printQueueRef.current.shift();
          setQueueLength(printQueueRef.current.length);
          lastPrintCompletionRef.current = Date.now();

          if (printQueueRef.current.length > 0) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_CONFIG.minDelayBetweenPrintsMs));
          }
        } catch (error: any) {
          if (item.attempts < RETRY_CONFIG.maxAttempts) {
            // Leave item at head of queue and wait before retrying
            await new Promise((resolve) => setTimeout(resolve, RETRY_CONFIG.retryDelayMs));
          } else {
            // Max attempts reached — drop this item and continue with the rest
            printQueueRef.current.shift();
            setQueueLength(printQueueRef.current.length);
            if (printQueueRef.current.length > 0) {
              await new Promise((resolve) => setTimeout(resolve, RETRY_CONFIG.minDelayBetweenPrintsMs));
            }
          }
        }
      }
    } finally {
      isPrintingRef.current = false;
      setIsPrinting(false);
      setCurrentPrintOrderId(null);
    }
  }, []);

  const getQueueStatus = useCallback(() => {
    return {
      length: printQueueRef.current.length,
      // backward compatibility: some callers expect `queueLength`
      queueLength: printQueueRef.current.length,
      isPrinting: isPrintingRef.current,
      currentItem: printQueueRef.current[0] || null,
      queue: [...printQueueRef.current],
    };
  }, []);

  const clearQueue = useCallback(() => {
    printQueueRef.current = [];
    setQueueLength(0);
    lastAddTimeRef.current = 0;
    lastPrintCompletionRef.current = 0;
  }, []);

  return {
    isPrinting,
    queueLength,
    currentPrintOrderId,
    addToQueue,
    processQueue,
    getQueueStatus,
    clearQueue,
  };
};

export type { IPrintOrder, PrintQueueItem };
